import { Component, OnInit, OnDestroy, ViewChild } from '@angular/core';
import { DataStateChangeEvent, GridComponent, GridDataResult } from '@progress/kendo-angular-grid';
import { CompositeFilterDescriptor, State } from '@progress/kendo-data-query';
import { forkJoin, of, Subscription } from 'rxjs';
import { map } from 'rxjs/operators';
import { Code } from 'src/app/core/model/dictionary';
import { AppConfigService } from 'src/app/core/service/app-config.service';
import { CommunityProviderProgramService } from 'src/app/core/service/community-provider-program.service';
import { DictionaryService } from 'src/app/core/service/dictionary.service';
import { ExtensionService } from 'src/app/core/service/extension.service';
import { CommunityProviderProgramListService } from 'src/app/core/service/grid-data/grid-data-community-provider-program.service';
import { MessageHandlerService } from 'src/app/core/utility/message-handler.service';
import { SearchHelperService } from 'src/app/core/utility/search-helper.service';
import { CommunityProviderProgramEdit } from '../../model/community-provider-program';
import { saveAs } from '@progress/kendo-file-saver';
import { Papa } from 'ngx-papaparse';
import { sortBy } from 'lodash';
import { CommunityProviderProgramImportComponent } from '../community-provider-program-import/community-provider-program-import.component';
import { AuthenticationService } from 'src/app/core/authentication/authentication.service';

@Component({
  standalone: false,
  selector: 'app-community-provider-program-list',
  templateUrl: './community-provider-program-list.component.html',
  styleUrls: ['./community-provider-program-list.component.scss']
})
export class CommunityProviderProgramListComponent implements OnInit, OnDestroy
{
  @ViewChild(GridComponent) public grid: GridComponent;
  @ViewChild('importCommunityProviderPrograms') public importCommunityProviderPrograms: CommunityProviderProgramImportComponent;

  public pageSizes = [5, 10, 25, 50, 100];
  public deleteDialogOpened = false;
  public providers = [];
  public gridView: GridDataResult;
  public gridData: any = {};
  public total: number;
  public enableAdvanced = false;
  public state: State = {
    skip: 0,
    // Initial filter descriptor
    filter: {
      logic: 'and',
      filters: []
    },
    sort: [
      { field: 'name', dir: 'asc' }
    ]
  };
  public filterText = '';
  public isNew = false;
  public editDataItem: CommunityProviderProgramEdit;
  public selectedProgram: CommunityProviderProgramEdit;
  public programInUse: boolean;
  public isDirectoryManager = false;

  private listSubscription: Subscription;

  constructor(
    private communityProviderProgramListService: CommunityProviderProgramListService,
    private searchHelperService: SearchHelperService,
    private appConfigService: AppConfigService,
    private communityProviderProgramService: CommunityProviderProgramService,
    private messageHandlerService: MessageHandlerService,
    private papa: Papa,
    private authService: AuthenticationService,
    private extensionService: ExtensionService,
    private dictionaryService: DictionaryService
  )
  {
    this.state.take = this.appConfigService.getConfig().pageSize;
    this.isDirectoryManager = (this.authService.currentUser.role === 'ROLE_DIRECTORY_MANAGER');
  }

  /**
   * Provide loading property from Service.
   */
  public get loading(): boolean
  {
    return this.communityProviderProgramListService.loading;
  }

  public ngOnInit(): void
  {
    this.listSubscription = this.communityProviderProgramListService.subscribe({
      next: (value: any) =>
      {
        if (value !== null)
        {
          value.data.forEach(d =>
          {
            d.serviceTypeName = d.serviceCategory.serviceType.displayName;
            d.categoryName = d.serviceCategory.name;
            d.channelName = d.channel ? d.channel.name : null;
            d.resourceStatusName = d.resourceStatus ? d.resourceStatus.displayName : null;
          });

          this.providers = value.data;
          this.gridView = {
            data: (value) ? this.gridData = value.data : this.gridData = [],
            total: (value) ? this.total = value.total : this.total = 0
          };
        }
      }
    });
    this.communityProviderProgramListService.queryByState(this.state);
  }

  /**
   * Data change event handler.
   *
   * @param state State of grid operations
   */
  public dataStateChange(state: DataStateChangeEvent): void
  {
    this.state = state;
    this.communityProviderProgramListService.queryByState(state);
  }

  public ngOnDestroy(): void
  {
    if (this.listSubscription !== undefined) { this.listSubscription.unsubscribe(); }
  }

  /**
   * Filtering by search field
   */
  public filterChange(currentFilter: CompositeFilterDescriptor): void
  {
    this.state.filter = currentFilter;
    this.communityProviderProgramListService.queryByState(this.state);
  }

  public searchFieldChange(filterText: string): void
  {
    this.state.skip = 0;
    const root = this.searchHelperService.fieldChange(filterText, this.state);
    this.filterText = filterText;
    this.filterChange(root);
  }

  public addHandler(): void
  {
    this.isNew = true;
    setTimeout(() =>
    {
      this.editDataItem = new CommunityProviderProgramEdit();
    });
  }

  /**
   * Closes a component
   *
   * @param component Name of component to be closed.
   */
  public close(component: string): void
  {
    this[component + 'Opened'] = false;
  }

  /**
   * Opens a component
   *
   * @param component Name of component to be opened.
   */
  public open(component: string): void
  {
    this[component + 'Opened'] = true;
  }

  public saveChangesHandler(): void
  {
    this.editDataItem = undefined;
    this.state.skip = 0;
    this.communityProviderProgramListService.queryByState(this.state);
  }

  public cancelChangesHandler(): void
  {
    this.editDataItem = undefined;
  }

  public editHandler(id: number): void
  {
    this.isNew = false;
    this.communityProviderProgramService.getCommunityProviderProgramById(id).subscribe({
      next: (data) =>
      {
        this.editDataItem = data;
      },
      error: (errorResponse) =>
      {
        this.messageHandlerService.displayError(errorResponse);
      }
    });
  }

  /**
   * Sets the user to delete, and opens the delete user dialog.
   */
  public deleteHandler(id: number): void
  {
    // Check if in use
    this.communityProviderProgramService.existsCommunityProviderProgram(id).subscribe({
      next: (inUseResponse) =>
      {
        this.programInUse = inUseResponse;
        this.communityProviderProgramService.getCommunityProviderProgramById(id).subscribe({
          next: (data) =>
          {
            this.selectedProgram = data as CommunityProviderProgramEdit;
            this.open('deleteDialog');
          },
          error: (errorResponse) =>
          {
            this.messageHandlerService.displayError(errorResponse);
          }
        });
      },
      error: (errorResponse) =>
      {
        this.messageHandlerService.displayError(errorResponse);
      }
    });
  }

  /**
   * Button action handler.
   *
   * @param confirm Confirm the action: yes/no
   */
  public deleteAction(confirm: boolean): void
  {
    if (confirm)
    {
      // Proceed with the deletion
      const providerProgramId = this.selectedProgram.id;
      this.communityProviderProgramService.deleteCommunityProviderProgram(providerProgramId).subscribe({
        next: () =>
        {
          this.messageHandlerService.displaySuccess('PROGRAM-DELETED-SUCCESSFULLY');
          this.state.skip = 0;
          this.communityProviderProgramListService.queryByState(this.state);
        },
        error: (errorResponse) =>
        {
          this.messageHandlerService.displayError(errorResponse);
        }
      });
    }
    // post-deletion cleanup and refresh grid
    this.close('deleteDialog');
    this.selectedProgram = null;
  }

  /**
   * Export the selected code set to a CSV file, and trigger a browser download.
   */
  public exportCommunityProvidersToCsv(): void
  {
    this.communityProviderProgramService.getAllCommunityProviderProgram().subscribe({
      next: (data) =>
      {
        const codeSetIds = new Set<number>();
        data.forEach((d: any) =>
        {
          d.extensions?.forEach((extension: any) =>
          {
            if (this.extensionService.getExtensionDataType(extension.settingExtension) === 'DICTIONARY')
            {
              const codeSetId = extension.settingExtension?.codeSetId ?? extension.codeSetId;
              if (codeSetId != null)
              {
                codeSetIds.add(codeSetId);
              }
            }
          });
        });

        const codeSetLoads = [...codeSetIds].map((id) =>
          this.dictionaryService.getCachedCodesByCodeSetId(id).pipe(
            map((codes) => ({ id, codes }))
          )
        );
        const loadCodes$ = codeSetLoads.length
          ? forkJoin(codeSetLoads)
          : of([] as Array<{ id: number; codes: Code[] }>);

        loadCodes$.subscribe({
          next: (codeSetResults) =>
          {
            const codesByCodeSetId = new Map<number, Code[]>();
            codeSetResults.forEach(({ id, codes }) => codesByCodeSetId.set(id, codes));
            this.buildCommunityProgramsCsvDownload(data, codesByCodeSetId);
          },
          error: (errorResponse) =>
          {
            this.messageHandlerService.displayError(errorResponse);
          }
        });
      }
    });
  }

  public openCommunityProviderProgramsImport(): void
  {
    this.importCommunityProviderPrograms.execute();
  }

  public refreshGrid(): void
  {
    this.communityProviderProgramListService.queryByState(this.state);
  }

  private buildCommunityProgramsCsvDownload(data: any[], codesByCodeSetId: Map<number, Code[]>): void
  {
        const communityPrograms = [];
        data.forEach((d: any) =>
        {
          let opening = '';
          let languages = '';
          let areaServedCities = '';
          let areaServedCounties = '';
          let areaServedZipCodes = '';
          let incomeRequirements = '';
          let paymentMethods = '';
          let licenses = '';
          let targetedDemographics = '';
          let targetedPopulations = '';
          let providerTaxonomies = '';
          let otherIdentifiers = '';
          let websites = '';
          let waitTimes = '';
          let enhancedServices = '';
          let extensions = '';
          let serviceCategory = '';

          if (d.openingTimes !== undefined && d.openingTimes !== null)
          {
            d.openingTimes.forEach(o =>
            {
              opening += o.day + '^' + o.from + '-' + o.to + '|';
            });
            opening = opening.slice(0, -1);
          }

          if (d.languages !== undefined && d.languages !== null)
          {
            d.languages.forEach(o =>
            {
              languages += o.code + '|';
            });
            languages = languages.slice(0, -1);
          }

          // Area Served Cities
          if (d.areaServedCities !== undefined && d.areaServedCities !== null)
          {
            d.areaServedCities.forEach(o =>
            {
              areaServedCities += o.code + '|';
            });
            areaServedCities = areaServedCities.slice(0, -1);
          }

          // Area Served Counties
          if (d.areaServedCounties !== undefined && d.areaServedCounties !== null)
          {
            d.areaServedCounties.forEach(o =>
            {
              areaServedCounties += o.code + '|';
            });
            areaServedCounties = areaServedCounties.slice(0, -1);
          }

          // Area Served Zip Codes
          if (d.areaServedZipCodes !== undefined && d.areaServedZipCodes !== null)
          {
            d.areaServedZipCodes.forEach(o =>
            {
              areaServedZipCodes += o.code + '|';
            });
            areaServedZipCodes = areaServedZipCodes.slice(0, -1);
          }

          // Income Requirements
          if (d.incomeRequirements !== undefined && d.incomeRequirements !== null)
          {
            d.incomeRequirements.forEach(o =>
            {
              incomeRequirements += o.code + '|';
            });
            incomeRequirements = incomeRequirements.slice(0, -1);
          }

          // Payment Methods
          if (d.paymentMethods !== undefined && d.paymentMethods !== null)
          {
            d.paymentMethods.forEach(o =>
            {
              paymentMethods += o.code + '|';
            });
            paymentMethods = paymentMethods.slice(0, -1);
          }

          // Licenses
          if (d.licenses !== undefined && d.licenses !== null)
          {
            d.licenses.forEach(o =>
            {
              licenses += o.code + '|';
            });
            licenses = licenses.slice(0, -1);
          }

          // Targeted Demographics
          if (d.targetedDemographics !== undefined && d.targetedDemographics !== null)
          {
            d.targetedDemographics.forEach(o =>
            {
              targetedDemographics += o.code + '|';
            });
            targetedDemographics = targetedDemographics.slice(0, -1);
          }

          // Targeted Populations
          if (d.targetedPopulations !== undefined && d.targetedPopulations !== null)
          {
            d.targetedPopulations.forEach(o =>
            {
              targetedPopulations += o.code + '|';
            });
            targetedPopulations = targetedPopulations.slice(0, -1);
          }

          // Provider Taxonomies
          if (d.providerTaxonomies !== undefined && d.providerTaxonomies !== null)
          {
            d.providerTaxonomies.forEach(o =>
            {
              providerTaxonomies += o.code + '|';
            });
            providerTaxonomies = providerTaxonomies.slice(0, -1);
          }

          // Other Identifiers
          if (d.otherIdentifiers !== undefined && d.otherIdentifiers !== null)
          {
            d.otherIdentifiers.forEach(o =>
            {
              otherIdentifiers += (o.assigningAuthorityDictionary.id !== 0 ? o.assigningAuthorityDictionary.code : '') + '^' + o.value + '^' + o.typeDictionary.code + '|';
            });
            otherIdentifiers = otherIdentifiers.slice(0, -1);
          }

          // Wait Times
          if (d.waitTimes !== undefined && d.waitTimes !== null)
          {
            d.waitTimes.forEach(o =>
            {
              waitTimes += o.locationID + '^' + o.waitTime + '|';
            });
            waitTimes = waitTimes.slice(0, -1);
          }

          // Websites
          if (d.websites !== undefined && d.websites !== null)
          {
            d.websites.forEach(o =>
            {
              websites += o.name + '^' + o.url + '|';
            });
            websites = websites.slice(0, -1);
          }

          // Enhanced Service Eligibility
          if (d.enhancedServices !== undefined && d.enhancedServices !== null)
          {
            d.enhancedServices.forEach(o =>
            {
              enhancedServices += o.code + '|';
            });
            enhancedServices = enhancedServices.slice(0, -1);
          }

          // Extensions
          if (d.extensions !== undefined && d.extensions !== null)
          {
            d.extensions.forEach(o =>
            {
              extensions += o.settingExtension.key + '^' +
                this.extensionService.formatExtensionValueForExport(o, codesByCodeSetId) + '|';
            });
            extensions = extensions.slice(0, -1);
          }

          // Service Category
          if (d.serviceCategory !== undefined && d.serviceCategory !== null)
          {
            serviceCategory = d.serviceCategory.serviceType.code + '^' + d.serviceCategory.name;
          }

          const csvRecord: any = {};
          csvRecord.id = d.id;
          csvRecord.providerName = d.organization.name;
          csvRecord.organizationType = d.organization.organizationType.code;     
          csvRecord.serviceName = d.name;
          csvRecord.cost = d.cost;
          csvRecord.description = d.description;
          csvRecord.eligibility = d.eligibility;
          csvRecord.openingTimes = opening;
          csvRecord.category = serviceCategory;
          csvRecord.languages = languages;
          csvRecord.addressLine1 = d.address && d.address.streetLine ? d.address.streetLine : '';
          csvRecord.addressLine2 = d.address && d.address.streetLineAdditional ? d.address.streetLineAdditional : '';
          csvRecord.city = d.address && d.address.city ? d.address.city : '';
          csvRecord.state = d.address && d.address.state ? d.address.state.code : '';
          csvRecord.postalCode = d.address && d.address.postalCode ? d.address?.postalCode : '';
          csvRecord.country = d.address && d.address.country ? d.address.country.code : '';
          csvRecord.longitude = d.address && d.address.longitude ? d.address.longitude : '';
          csvRecord.latitude = d.address && d.address.latitude ? d.address.latitude : '';
          csvRecord.programContactName = d.programContactName;
          csvRecord.programPhoneNumber = d.phoneNumber ? d.phoneNumber : '';
          csvRecord.programFaxNumber = d.faxNumber ? d.faxNumber : '';
          csvRecord.programEmail = d.email ? d.email : '';
          csvRecord.programUrl = d.url ? d.url : '';
          csvRecord.referralContactName = d.referralContactName;
          csvRecord.referralPhoneNumber = d.referralPhoneNumber ? d.referralPhoneNumber : '';
          csvRecord.referralFaxNumber = d.referralFaxNumber ? d.referralFaxNumber : '';
          csvRecord.referralEmail = d.referralEmail ? d.referralEmail : '';
          csvRecord.referralUrl = d.referralUrl ? d.referralUrl : '';
          csvRecord.referralEmergencyNumber = d.referralEmergencyNumber ? d.referralEmergencyNumber : '';
          csvRecord.referralExtension = d.referralExtension ? d.referralExtension : '';
          csvRecord.alias = d.alias;
          csvRecord.alertDescription = d.alertDescription;
          csvRecord.applicationProcess = d.applicationProcess;
          csvRecord.capacity = d.capacity;
          csvRecord.documentationRequired = d.documentationRequired;
          csvRecord.acceptingDisabledPatients = d.acceptingDisabledPatients;
          csvRecord.acceptingElectronicReferrals = d.acceptingElectronicReferrals;
          csvRecord.acceptingNewPatients = d.acceptingNewPatients;
          csvRecord.acceptingSelfReferrals = d.acceptingSelfReferrals;          
          csvRecord.lgbtq = d.lgbtq;
          csvRecord.submittingOrganization = d.submittingOrganization ? d.submittingOrganization.name : '';
          csvRecord.resourceStatus = d.resourceStatus ? d.resourceStatus.code : '';
          csvRecord.areaServedCities = areaServedCities;
          csvRecord.areaServedCounties = areaServedCounties;
          csvRecord.areaServedZipCodes = areaServedZipCodes;
          csvRecord.incomeRequirements = incomeRequirements;
          csvRecord.paymentMethods = paymentMethods;
          csvRecord.licenses = licenses;
          csvRecord.targetedDemographics = targetedDemographics;
          csvRecord.targetedPopulations = targetedPopulations;
          csvRecord.providerTaxonomies = providerTaxonomies;
          csvRecord.otherIdentifiers = otherIdentifiers;
          csvRecord.waitTimes = waitTimes;
          csvRecord.websites = websites;
          csvRecord.directoryManagerName = d.directoryManagerName;
          csvRecord.directoryManagerEmail = d.directoryManagerEmail;
          csvRecord.directoryManagerNotificationPreference = d.directoryManagerNotificationPreference;
          csvRecord.enhancedServices = enhancedServices;
          csvRecord.ableToConductScreenings = d.ableToConductScreenings;
          csvRecord.informationalOnly = d.informationalOnly;
          csvRecord.allowDuplicateReferrals = d.allowDuplicateReferrals;
          csvRecord.extensions = extensions;          

          communityPrograms.push(csvRecord);
        });
        const csvContent = this.papa.unparse(sortBy(communityPrograms, ['programName']));
        const csvFileName = 'CommunityServices.csv';

        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        saveAs(url, csvFileName);
  }
}
